/**
 * Платные наборы: что внутри и как выдаётся в сохранение.
 * Цены лежат в src/pay/packs.json (общий файл с сервером оплаты).
 * Выдача идёт напрямую в состояние: монеты из набора НЕ считаются «заработанными» (не влияют на рейтинг).
 */
import type { GameState, L10n } from './types';
import PRICES from '../pay/packs.json';
import { applyGrants, type PackGrants, type PackResult } from './grants';
import { ensurePass } from './season';

export type { PackGrants, PackResult };

export type PackKind = 'regular' | 'newbie' | 'pass';
export interface PackDef {
  id: string;
  rub: number;
  icon: string;
  name: L10n;
  tag?: 'popular' | 'best' | 'hit';
  kind: PackKind;
  grants: PackGrants;
}

/** Окно предложения для новичков: первые 7 суток с создания острова. */
export const NEWBIE_WINDOW_MS = 7 * 86_400_000;

type PackDraft = Omit<PackDef, 'rub' | 'kind'> & { kind?: PackKind };

const DRAFTS: PackDraft[] = [
  { id: 'pk_newbie', icon: '🎁', kind: 'newbie', tag: 'hit',
    name: { zh: '新岛民礼包', en: 'Newcomer Pack', ru: 'Набор новичка' },
    grants: { coins: 40_000, food: 1000, fullEnergy: true, rodMax: true, islandTiers: 1,
      pets: ['pet_puppy', 'pet_kitten'], animals: ['animal_chicken', 'animal_duck', 'animal_rabbit', 'animal_goat'],
      clothing: ['cl_hat_straw', 'cl_top_swim', 'cl_bottom_overalls', 'cl_acc_glasses'] } },
  { id: 'pk_pass', icon: '🎫', kind: 'pass',
    name: { zh: '赛季通行证', en: 'Season Pass', ru: 'Сезонный пропуск' },
    grants: { passPremium: true } },
  { id: 'pk_700', icon: '🎣', tag: 'popular',
    name: { zh: '启程渔获', en: 'Starter Catch', ru: 'Стартовый улов' },
    grants: { coins: 20_000, food: 400, fullEnergy: true, pets: ['pet_puppy'], clothing: ['cl_hat_straw', 'cl_acc_scarf'] } },
  { id: 'pk_800', icon: '🐐',
    name: { zh: '牧场礼包', en: 'Farmer Pack', ru: 'Фермерский набор' },
    grants: { coins: 25_000, food: 600, fullEnergy: true, animals: ['animal_goat', 'animal_pig', 'animal_sheep'] } },
  { id: 'pk_900', icon: '👗',
    name: { zh: '潮流造型', en: 'Fashion Look', ru: 'Модный образ' },
    grants: { coins: 22_000, clothing: ['cl_top_hawaii', 'cl_bottom_grass', 'cl_hat_flower', 'cl_acc_shell', 'cl_hair_curly_pink'] } },
  { id: 'pk_1000', icon: '🏆', tag: 'best',
    name: { zh: '钓王礼包', en: 'Angler Pack', ru: 'Рыбацкий набор' },
    grants: { coins: 30_000, food: 800, xp: 300, fullEnergy: true, rodMax: true, pets: ['pet_kitten'] } },
  { id: 'pk_1100', icon: '🐾',
    name: { zh: '毛茸茸伙伴', en: 'Furry Friends', ru: 'Пушистые друзья' },
    grants: { coins: 28_000, pets: ['pet_bunny', 'pet_piglet', 'pet_turtle'] } },
  { id: 'pk_1500', icon: '🏠',
    name: { zh: '岛主之家', en: 'Island Owner', ru: 'Хозяин острова' },
    grants: { coins: 55_000, food: 1500, fullEnergy: true, islandTiers: 1, buildings: ['bld_beachhouse', 'bld_windmill'] } },
  { id: 'pk_2000', icon: '🏗️',
    name: { zh: '建造大师', en: 'Master Builder', ru: 'Мастер-стройка' },
    grants: { coins: 80_000, islandTiers: 1, buildings: ['bld_villa', 'bld_statue', 'bld_banyan'] } },
  { id: 'pk_2200', icon: '👑',
    name: { zh: '皇家衣橱', en: 'Royal Wardrobe', ru: 'Королевский гардероб' },
    grants: { coins: 60_000, clothing: ['cl_top_royal', 'cl_hat_crown', 'cl_acc_wings', 'cl_top_pirate', 'cl_hat_pirate', 'cl_acc_parrot', 'cl_top_kimono'] } },
  { id: 'pk_2500', icon: '🦚',
    name: { zh: '百兽牧场', en: 'Menagerie', ru: 'Звериный двор' },
    grants: { coins: 100_000, animals: ['animal_cow', 'animal_turkey', 'animal_alpaca'], pets: ['pet_parrot', 'pet_fox'] } },
  { id: 'pk_3000', icon: '🏝️', tag: 'hit',
    name: { zh: '天堂小岛', en: 'Paradise Isle', ru: 'Райский остров' },
    grants: { coins: 250_000, food: 5000, fullEnergy: true, rodMax: true, islandTiers: 2,
      buildings: ['bld_castle', 'bld_lighthouse', 'bld_hotspring'], pets: ['pet_dolphin'], clothing: ['cl_top_royal', 'cl_hat_crown'] } },
];

const priceTable = (PRICES as { prices: Record<string, number> }).prices;
export const PACKS: PackDef[] = DRAFTS.map((d) => ({ ...d, kind: d.kind ?? 'regular', rub: priceTable[d.id] ?? 0 }));
export const packById = (id: string): PackDef | undefined => PACKS.find((p) => p.id === id);

/**
 * Первая покупка каждого набора: монеты ×2 (бонус показывается на карточке).
 * Набор новичка разовый, поэтому удвоения у него нет — он и так выгоднее остальных.
 */
export function firstPurchaseBonus(state: GameState, pack: PackDef): boolean {
  return pack.kind === 'regular' && !(state.purchases?.[pack.id]);
}

/** Сколько осталось до конца предложения для новичков (мс); 0 — предложение закончилось или уже куплено. */
export function newbieTimeLeft(state: GameState, now: number): number {
  if (state.purchases?.pk_newbie) return 0;
  return Math.max(0, state.createdAt + NEWBIE_WINDOW_MS - now);
}

/** Доступен ли набор игроку сейчас (набор новичка — только в первую неделю и один раз). */
export function packAvailable(state: GameState, pack: PackDef, now: number): boolean {
  return pack.kind !== 'newbie' || newbieTimeLeft(state, now) > 0;
}

/** Выдать набор в сохранение. Если для предмета нет места на острове — вместо него начисляются монеты. */
export function applyPack(state: GameState, pack: PackDef, now: number, petName: (defId: string) => string): PackResult {
  const doubled = firstPurchaseBonus(state, pack);
  const grants: PackGrants = doubled && pack.grants.coins ? { ...pack.grants, coins: pack.grants.coins * 2 } : pack.grants;
  const res = applyGrants(state, grants, now, petName);
  if (grants.passPremium) ensurePass(state, now).premium = true;
  state.purchases = state.purchases ?? {};
  state.purchases[pack.id] = (state.purchases[pack.id] ?? 0) + 1;
  return res;
}
